import { apiV2, errorHandler } from "./index";
import { API_ERROR } from "../constants/ActionTypes";
import Swal from "sweetalert2";

/**
 * Trigger an anonymized research feedback export.
 * Returns the created job (status=pending) for polling.
 */
export function createFeedbackExport(filters = {}) {
  return function (dispatch) {
    return apiV2
      .post("/research/feedback-exports", filters)
      .then((response) => {
        const job = response.data?.data || response.data;
        return Promise.resolve(job);
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.reject(error);
      });
  };
}

/**
 * Poll an export job's status.
 */
export function checkFeedbackExportStatus(jobId) {
  return function (dispatch) {
    return apiV2
      .get(`/research/feedback-exports/${jobId}`)
      .then((response) => {
        return Promise.resolve(response.data?.data || response.data);
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.resolve(null);
      });
  };
}

/**
 * List past feedback exports (paginated).
 */
export function fetchFeedbackExports() {
  return function (dispatch) {
    return apiV2
      .get("/research/feedback-exports")
      .then((response) => {
        return Promise.resolve(response.data?.data || []);
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.resolve([]);
      });
  };
}

/**
 * Download a completed feedback export.
 *
 * Streams the gzipped CSV from the API as a blob and triggers a save.
 * Uses Bearer auth (apiV2 instance has the interceptor) — no signed URL needed
 * because the file is small and the API endpoint streams from S3.
 */
export function downloadFeedbackExport(jobId, fileName) {
  return function (dispatch) {
    return apiV2
      .get(`/research/feedback-exports/${jobId}/download`, {
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/gzip" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || `research_feedback_export_${jobId}.csv.gz`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return { success: true };
      })
      .catch(async (error) => {
        if (error._sessionExpired) {
          return { success: false };
        }

        // With responseType: "blob", error.response.data is a Blob too — extract
        // the JSON message before showing it (otherwise it's always undefined).
        let message = "Could not download the feedback export.";
        const blob = error.response?.data;
        if (blob instanceof Blob) {
          try {
            const text = await blob.text();
            try {
              const parsed = JSON.parse(text);
              message = parsed.message || parsed.error || message;
            } catch (_jsonErr) {
              if (text) message = text;
            }
          } catch (_blobErr) {
            // fall through with default message
          }
        } else if (error.response?.data?.message) {
          message = error.response.data.message;
        }

        Swal.fire({
          title: "Download failed",
          text: message,
          icon: "error",
        });
        return { success: false };
      });
  };
}
